import React from 'react';
import { Box, FormControl, InputLabel, MenuItem, Select, Typography } from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers';
import TouchTooltip from '../../common/TouchTooltip';
import { LIVE_INITIAL_IMPORT_NOTE, LIVE_TOOLTIP } from './constants';
import { fromYmd, toYmd } from '../../../utils/buyerInsightsDates';
import { liveFilterSummary } from '../../../utils/buyerInsightsFormat';

const MODES = [
  { value: 'any', label: 'Any time' },
  { value: 'between', label: 'Between' },
  { value: 'after', label: 'After' },
  { value: 'before', label: 'Before' },
];

// "Product live date" (spec §0.5): Any time · Between (Live from / Live until) · After (Live after)
// · Before (Live before). Values are YYYY-MM-DD strings. Must be rendered inside a LocalizationProvider.
const ProductLiveDateFilter = ({ value, onChange, hasInitialImportProducts, isWide, disabled }) => {
  const mode = value.mode || 'any';
  const showFrom = mode === 'between' || mode === 'after';
  const showTo = mode === 'between' || mode === 'before';
  const tooltip = hasInitialImportProducts ? `${LIVE_TOOLTIP} ${LIVE_INITIAL_IMPORT_NOTE}` : LIVE_TOOLTIP;
  const summary = liveFilterSummary(value);
  const fieldSx = { minWidth: isWide ? 170 : '100%', '& .MuiInputBase-root': { minHeight: isWide ? 40 : 44 } };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
        <FormControl size="small" sx={fieldSx} disabled={disabled}>
          <InputLabel id="live-date-mode">Product live date</InputLabel>
          <Select
            labelId="live-date-mode"
            label="Product live date"
            value={mode}
            onChange={(e) => onChange({ mode: e.target.value, from: value.from || '', to: value.to || '' }, 'live_mode')}
          >
            {MODES.map((m) => (
              <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {showFrom && (
          <DatePicker
            label={mode === 'after' ? 'Live after' : 'Live from'}
            value={value.from ? fromYmd(value.from) : null}
            onChange={(d) => d && onChange({ ...value, mode, from: toYmd(d) }, 'live_from')}
            disabled={disabled}
            slotProps={{ textField: { size: 'small', sx: fieldSx } }}
          />
        )}
        {showTo && (
          <DatePicker
            label={mode === 'before' ? 'Live before' : 'Live until'}
            value={value.to ? fromYmd(value.to) : null}
            onChange={(d) => d && onChange({ ...value, mode, to: toYmd(d) }, 'live_to')}
            disabled={disabled}
            minDate={value.from ? fromYmd(value.from) : undefined}
            slotProps={{ textField: { size: 'small', sx: fieldSx } }}
          />
        )}
        <TouchTooltip title={disabled ? 'Not used in Brands you don’t carry' : tooltip}>
          <InfoOutlined sx={{ fontSize: 18, color: 'text.disabled', cursor: 'help' }} />
        </TouchTooltip>
      </Box>
      {summary && (
        <Typography variant="caption" color="text.secondary">
          {summary}
        </Typography>
      )}
    </Box>
  );
};

export default ProductLiveDateFilter;
